import { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, RefreshCcw } from "lucide-react";
import PacificClock from "@/components/PacificClock";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "omnimuscle_daily_tip_flip_v1"; // tracks { date, flipped }

const CATEGORY_COLORS = {
  Training: "#DC2626",
  Nutrition: "#D4AF37",
  Mindset: "#4C1D95",
  Recovery: "#0EA5E9",
  Spirit: "#A855F7",
};

function readStored() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

function writeStored(v) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(v));
  } catch {
    // ignore
  }
}

export default function DailyTip() {
  const { refreshUser, user } = useAuth();
  const [tip, setTip] = useState(null);
  const [flipped, setFlipped] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/tip-of-the-day`)
      .then((r) => {
        if (cancelled) return;
        setTip(r.data);
        const stored = readStored();
        if (stored.date === r.data.date && stored.flipped) {
          setFlipped(true);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const flip = () => {
    setFlipped(true);
    if (tip) writeStored({ date: tip.date, flipped: true });
    // Claim 1 ✨ Spark Phoenix Token for flipping (once per UTC day, server-enforced)
    if (user && user !== false) {
      axios
        .post(`${API}/tokens/claim-daily-tip`)
        .then((r) => {
          if (!r.data?.already_claimed) refreshUser?.();
        })
        .catch(() => {});
    }
  };

  const flipBack = () => {
    setFlipped(false);
    if (tip) writeStored({ date: tip.date, flipped: false });
  };

  if (loading || !tip) return null;

  const accent = CATEGORY_COLORS[tip.category] || "#D4AF37";

  return (
    <section
      id="daily-tip"
      data-testid="daily-tip"
      className="py-20 md:py-28 bg-[#0A0A0A] relative overflow-hidden"
    >
      {/* Backdrop accents */}
      <div className="absolute inset-0 pointer-events-none opacity-30">
        <div
          className="absolute -top-32 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full blur-[160px]"
          style={{ backgroundColor: accent }}
        />
      </div>

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-bold mb-2 inline-flex items-center gap-2">
          <Sparkles size={12} /> Roc's Daily Drop
        </div>
        <h2 className="font-display text-4xl sm:text-5xl text-white leading-[0.95] mb-3">
          Today's <span className="text-[#D4AF37]">Tip.</span>
        </h2>
        <p className="text-xs text-neutral-500 uppercase tracking-widest mb-3">
          {new Date(tip.date + "T00:00:00").toLocaleDateString(undefined, {
            weekday: "long",
            month: "long",
            day: "numeric",
          })}
          {"  ·  "}
          #{tip.index + 1} of {tip.total_in_pool}
        </p>
        <div className="mb-8 flex justify-center">
          <PacificClock size="sm" testId="daily-tip-clock" showDate={false} />
        </div>

        {/* The card */}
        <div
          className="relative mx-auto"
          style={{ perspective: "1200px", maxWidth: "640px" }}
        >
          <div
            data-testid="daily-tip-card"
            onClick={flipped ? flipBack : flip}
            className="relative w-full cursor-pointer transition-transform duration-700"
            style={{
              transformStyle: "preserve-3d",
              transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
              minHeight: "320px",
            }}
            role="button"
            aria-pressed={flipped}
            aria-label="Flip daily tip card"
          >
            {/* FRONT */}
            <div
              data-testid="daily-tip-front"
              className="absolute inset-0 bg-gradient-to-br from-[#1a0d2b] to-[#0F0F0F] border-2 flex flex-col items-center justify-center p-10 group hover:shadow-[0_0_40px_rgba(212,175,55,0.3)] transition-shadow"
              style={{
                backfaceVisibility: "hidden",
                borderColor: "#D4AF37",
              }}
            >
              <div className="text-7xl mb-4">🃏</div>
              <div className="font-display text-3xl md:text-4xl text-white mb-2">
                Tap to Reveal
              </div>
              <div className="text-sm text-neutral-400 max-w-md">
                One fresh tip from Roc — every day at midnight UTC. Mind · Body
                · Soul.
              </div>
              <div className="absolute top-4 right-4 text-[#D4AF37] text-xs font-bold uppercase tracking-widest opacity-60 group-hover:opacity-100 transition-opacity">
                OMNI<span className="text-[#DC2626]">MUSCLE</span>
              </div>
            </div>

            {/* BACK */}
            <div
              data-testid="daily-tip-back"
              className="absolute inset-0 bg-[#0F0F0F] border-2 flex flex-col p-8 md:p-10 text-left"
              style={{
                backfaceVisibility: "hidden",
                transform: "rotateY(180deg)",
                borderColor: accent,
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <span
                  data-testid="daily-tip-category"
                  className="text-[10px] uppercase tracking-[0.3em] font-bold px-2 py-1 border"
                  style={{ color: accent, borderColor: accent }}
                >
                  {tip.category}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    flipBack();
                  }}
                  className="text-xs text-neutral-500 hover:text-[#D4AF37] inline-flex items-center gap-1 transition-colors"
                  title="Flip back"
                  data-testid="daily-tip-flipback"
                >
                  <RefreshCcw size={12} /> Hide
                </button>
              </div>
              <h3
                data-testid="daily-tip-title"
                className="font-display text-3xl md:text-4xl text-white leading-tight mb-4"
              >
                {tip.title}
              </h3>
              <p
                data-testid="daily-tip-body"
                className="text-base md:text-lg text-neutral-300 leading-relaxed flex-1"
              >
                {tip.body}
              </p>
              <div className="mt-6 pt-4 border-t border-zinc-800 flex items-center justify-between text-xs text-neutral-500">
                <span className="uppercase tracking-widest">— Roc</span>
                <span>Come back tomorrow for a new drop</span>
              </div>
            </div>
          </div>
        </div>

        <p className="text-[10px] text-neutral-600 mt-6 uppercase tracking-widest">
          New tip every day · {tip.total_in_pool} total in the deck
        </p>
      </div>
    </section>
  );
}
